import { Component, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';

import { DataService } from '../../services/data.service';

export type RuleStatus = 'Active' | 'Draft' | 'Archived';

export interface SubjectRequirement {
  name: string;
  minMarks: number | null;
  mandatory: boolean;
}

export interface CategorySeat {
  category: string;
  totalSeats: number | null;
  reservedPct: number | null;
}

export interface DocumentRequirement {
  name: string;
  mandatory: boolean;
}

export interface EligibilityRule {
  id: number;
  ruleName: string;
  programClass: string;
  stream: string;
  academicYear: string;
  status: RuleStatus;

  // Age criteria
  minAge: number | null;
  maxAge: number | null;
  ageAsOnDate: string;

  // Academic criteria
  minPercentage: number | null;
  requiredBoard: string;
  passingYearFrom: number | null;
  passingYearTo: number | null;
  graceMarks: number | null;

  // Subject requirements
  subjectRequirements: SubjectRequirement[];

  // Category seats
  categorySeats: CategorySeat[];

  // Documents
  documents: DocumentRequirement[];

  // Entrance / test
  entranceRequired: boolean;
  entranceExamName: string;
  minEntranceScore: number | null;

  // Other
  domicileRequired: boolean;
  medicalFitness: boolean;
  remarks: string;
}

@Component({
  selector: 'app-eligibility-setup',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink],
  templateUrl: './eligibility-setup.html',
  styleUrl: './eligibility-setup.scss',
})
export class EligibilitySetup {
  activeTab = signal<'list' | 'form'>('list');
  submitted = signal(false);
  saveSuccess = signal(false);
  editingId = signal<number | null>(null);
  searchQuery = '';
  filterStatus: RuleStatus | '' = '';
  filterClass = '';

  base = 'erules';		// important for API calls
  loading = false;
  allData: any = [];

  classOptions: any = [];
  streamOptions: any = [];
  boardOptions: any = [];
  academicYears: any = [];
  elig_status: any = [];
  allCategories: any = [];
  defaultDocuments: DocumentRequirement[] = [];

  rules = signal<EligibilityRule[]>([]);
  form: EligibilityRule = this.blankForm();

  private nextId = 4;

  constructor(public dataService: DataService) {
    //
  }

  ngOnInit() {
    for(let i=0; i < this.dataService.document_types.length; i++) {
      const nm: any = this.dataService.document_types[i].name;
      this.defaultDocuments.push({ name: nm, mandatory: false });
    }

    this.classOptions = this.dataService.classes;
    this.streamOptions = this.dataService.streams;
    this.academicYears = this.dataService.fin_period;
    this.elig_status = this.dataService.eligibility_status;
    this.boardOptions = this.dataService.boards;
    this.allCategories = this.dataService.categories;
    const categorySeats: any = this.allCategories.slice(0, 1).map((cat: any) => ({
      category: cat,
      totalSeats: null,
      reservedPct: null,
    }));

    this.loading = true;
    this.dataService.getAllRecords(this.base).then((res: any) => {
      for(let i=0; i<res.length; i++) {
        res[i].subject_marks = res[i].subject_marks?.map((item: any) => JSON.parse(item));
        res[i].category_seats = res[i].category_seats?.map((item: any) => JSON.parse(item));
        res[i].docs_req = res[i].docs_req?.map((item: any) => JSON.parse(item));
        let prog: any = res[i].eprogram?.split("-") || [];

        this.form.id = res[i].id;
        this.form.ruleName = res[i].rule_name;
        this.form.programClass = prog[0];
        this.form.stream = prog[1]=='na' ? '' : prog[1];
        this.form.academicYear = res[i].academic_year;
        this.form.status = res[i].status;
        this.form.remarks = res[i].remarks;
        this.form.minAge = res[i].min_age_years;
        this.form.maxAge = res[i].max_age_years;
        this.form.ageAsOnDate = res[i].age_date;
        this.form.minPercentage = res[i].min_percentage;
        this.form.graceMarks = res[i].grace_marks;
        this.form.requiredBoard = res[i].req_board;
        this.form.passingYearFrom = res[i].passing_year_from;
        this.form.passingYearTo = res[i].passing_year_to;
        this.form.entranceRequired = res[i].entrance_required;
        this.form.entranceExamName = res[i].entrance_exam_name;
        this.form.minEntranceScore = res[i].min_entrance_score;
        this.form.subjectRequirements = res[i].subject_marks || [{}];
        this.form.categorySeats = res[i].category_seats || categorySeats;
        this.form.documents = res[i].docs_req || this.defaultDocuments.map((d: any) => ({ ...d }))
      
        // insert in the existing data for screen
        const newRule: EligibilityRule = { ...this.form };
        this.rules.update(list => [...list, newRule]);
      }

      this.allData = res;
      this.loading = false;
    });
  }

  blankForm(): EligibilityRule {
    return {
      id: 0,
      ruleName: '',
      programClass: '',
      stream: 'N/A',
      academicYear: '2025-26',
      status: 'Draft',
      minAge: null,
      maxAge: null,
      ageAsOnDate: '2025-07-01',
      minPercentage: null,
      requiredBoard: 'Any',
      passingYearFrom: null,
      passingYearTo: null,
      graceMarks: 0,
      subjectRequirements: [{ name: '', minMarks: null, mandatory: false }],
      categorySeats: this.allCategories.slice(0, 5).map((cat: any) => ({
        category: cat,
        totalSeats: null,
        reservedPct: null,
      })),
      documents: this.defaultDocuments.map((d: any) => ({ ...d })),
      entranceRequired: false,
      entranceExamName: '',
      minEntranceScore: null,
      domicileRequired: false,
      medicalFitness: false,
      remarks: '',
    };
  }

  get filteredRules(): EligibilityRule[] {
    return this.rules().filter(r => {
      const q = this.searchQuery.toLowerCase();
      const matchSearch =
        !q ||
        r.ruleName.toLowerCase().includes(q) ||
        r.programClass.toLowerCase().includes(q) ||
        r.academicYear.includes(q);
      const matchStatus = !this.filterStatus || r.status === this.filterStatus;
      const matchClass = !this.filterClass || r.programClass === this.filterClass;
      return matchSearch && matchStatus && matchClass;
    });
  }

  get totalSeats(): number {
    return this.form.categorySeats?.reduce((s, c) => s + (c.totalSeats ?? 0), 0);
  }

  get grandTotalSeats(): number {
    return this.rules().reduce((sum, r) => sum + this.getTotalSeatsForRule(r), 0);
  }

  openNew(): void {
    this.form = this.blankForm();
    this.editingId.set(null);
    this.submitted.set(false);
    this.activeTab.set('form');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  editRule(rule: EligibilityRule): void {
    this.form = JSON.parse(JSON.stringify(rule));
    this.editingId.set(rule.id);
    this.submitted.set(false);
    this.activeTab.set('form');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  deleteRule(id: number): void {
    if (confirm('Are you sure you want to delete this eligibility rule?')) {
      this.rules.update(list => list.filter(r => r.id !== id));
    }
  }

  duplicateRule(rule: EligibilityRule): void {
    const copy: EligibilityRule = JSON.parse(JSON.stringify(rule));
    copy.id = this.nextId++;
    copy.ruleName = copy.ruleName + ' (Copy)';
    copy.status = 'Draft';

    this.form = copy;
    this.saveForm();
    // this.rules.update(list => [...list, copy]);
  }

  toggleStatus(rule: EligibilityRule): void {
    this.rules.update(list =>
      list.map(r =>
        r.id === rule.id
          ? { ...r, status: r.status === 'Active' ? 'Draft' : 'Active' }
          : r
      )
    );
  }

  addSubject(): void {
    this.form.subjectRequirements.push({ name: '', minMarks: null, mandatory: false });
  }

  removeSubject(i: number): void {
    this.form.subjectRequirements.splice(i, 1);
  }

  addCategoryRow(): void {
    const used = this.form.categorySeats.map(c => c.category);
    const next = this.allCategories.find((c: any) => !used.includes(c));
    if (next) {
      this.form.categorySeats.push({ category: next, totalSeats: null, reservedPct: null });
    }
  }

  removeCategoryRow(i: number): void {
    if (this.form.categorySeats.length > 1) {
      this.form.categorySeats.splice(i, 1);
    }
  }

  saveForm(): void {
    this.submitted.set(true);
    if (!this.form.ruleName || !this.form.programClass || !this.form.academicYear) {
      return;
    }

    const subjectRequirements = this.form.subjectRequirements?.map((item: any) => JSON.stringify(item));
    const categorySeats = this.form.categorySeats?.map((item: any) => JSON.stringify(item));
    const documents = this.form.documents?.map((item: any) => JSON.stringify(item));

    const myForm: any = {
      "rule_name": this.form.ruleName, 
      "eprogram": this.form.programClass + '-' + (this.form.stream || 'na'), 
      "academic_year": this.form.academicYear, 
      "status": this.form.status, 
      "remarks": this.form.remarks, 
      "min_age_years": this.form.minAge, 
      "max_age_years": this.form.maxAge, 
      "age_as_on_date": this.form.ageAsOnDate, 
      "min_percentage": this.form.minPercentage, 
      "grace_marks": this.form.graceMarks, 
      "req_board": this.form.requiredBoard, 
      "passing_year_from": this.form.passingYearFrom, 
      "passing_year_to": this.form.passingYearTo, 
      "entrance_required": this.form.entranceRequired, 
      "entrance_exam_name": this.form.entranceExamName, 
      "min_entrance_score": this.form.minEntranceScore, 
      "subject_marks": subjectRequirements || null, 
      "category_seats": categorySeats || null, 
      "docs_req": documents || null
    };

    if (this.editingId() !== null) {
      this.doUpdate(this.form.id, myForm);  // update into database

      // update in the existing data for screen
      this.rules.update(list =>
        list.map(r => (r.id === this.editingId() ? { ...this.form, id: r.id } : r))
      );
    } else {
      this.doSave(myForm);    // save into database

      // insert in the existing data for screen
      const newRule: EligibilityRule = { ...this.form, id: this.nextId++ };
      this.rules.update(list => [...list, newRule]);
    }
    this.saveSuccess.set(true);
    setTimeout(() => {
      this.saveSuccess.set(false);
      this.activeTab.set('list');
    }, 1800);
  }

  cancelForm(): void {
    this.activeTab.set('list');
    this.submitted.set(false);
  }

  getStatusClass(status: RuleStatus): string {
    return { Active: 'badge-active', Draft: 'badge-draft', Archived: 'badge-archived' }[status];
  }

  getTotalSeatsForRule(rule: EligibilityRule): number {
    return rule.categorySeats?.reduce((s, c) => s + (c.totalSeats ?? 0), 0);
  }

  activeRulesCount = computed(() => {
    const currentRules = this.rules();
    if (!currentRules) return 0;
    return currentRules.filter(r => r.status == 'Active').length;
  });

  draftRulesCount = computed(() => {
    const currentRules = this.rules();
    if (!currentRules) return 0;
    return currentRules.filter(r => r.status == 'Draft').length;
  });

  doSave(frmData: any) {
    this.loading = true;
    this.dataService.addNewRecord(this.base, frmData, true).subscribe({
      next: (response) => {
        // console.log('Response received in component:', response);
        this.loading = false;
      },
      error: (error) => {
        console.error('Error received in component:', error);
        this.loading = false;
      }
    });
  }

  doUpdate(myid: number, frmData: any) {
    this.loading = true;
    this.dataService.updateRecord(this.base, myid, frmData, true).subscribe({
      next: (response) => {
        // console.log('Response received in component:', response);
        this.loading = false;
      },
      error: (error) => {
        console.error('Error received in component:', error);
        this.loading = false;
      }
    });
  }

}
